import CryptoJS from "crypto-js";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import React from "react";

import { sections } from "./enums";

type DateInput = string | number | Date;

type ReplaceAllString = string & {
	replaceAll(searchValue: string | RegExp, replaceValue: string): ReplaceAllString;
};

type MonthYear = {
	month: number;
	year: number;
};

type MonthYearDay = MonthYear & {
	day: number;
};

type DateObject = {
	month: string | number;
	year: string | number;
	day: string | number;
};

export type ListItem = {
	bp: boolean;
	value: any;
};

type DprozPackage = {
	originalAmount: number;
	maxRedeemablePoints: number;
	packageDetail: string;
};

type RewardPoint = {
	rewardPoints: number;
	_id?: string;
	[key: string]: any;
};

type MaximumRedeemablePoints = {
	amountPaidByPoints: number;
	amount: number;
	originalAmount: number;
	rewards: RewardPoint[];
	totalAvailablePoints: number;
	redeemableRewardIds: string[];
};

type SocialLink = {
	type: string;
	value?: string;
};

type SplitSkill = {
	title: string;
	skills: Array<{
		added: any;
		skill: any;
	}>;
};

type IndexableObject = Record<string, any>;

type WindowWithOpera = Window & {
	opera?: string;
};

export const months: string[] = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export function monthToNumber(month: string): number {
	return months.indexOf(month) + 1;
}

export const maxPercentageRewardsPointsToPay: number = 0.615;
export const packageNames: Record<string, DprozPackage> = {
	silver: {
		originalAmount: 1950,
		maxRedeemablePoints: 1200,
		packageDetail: "Dproz Silver package. 14 days full access",
	},
	gold: { originalAmount: 5850, maxRedeemablePoints: 3600, packageDetail: "Dproz Gold package. 60 days full access" },
	platinum: {
		originalAmount: 51500,
		maxRedeemablePoints: 31650,
		packageDetail: "Dproz Platinum package. 12 months full access",
	},
};

/**
 * Parse a date the way the resume builder means it.
 *
 * `<input type="date">` hands over a date-only string such as `2026-03-01`, which
 * `new Date()` reads as UTC midnight. The helpers below then read it back with
 * *local* getters, so anywhere west of UTC the month rolled back by one: a job
 * starting March 2026 was shown — and saved — as February 2026.
 *
 * A date-only value carries no time zone, so parse it as a local calendar date.
 * Values that do carry a time (API timestamps) keep the normal parsing.
 */
function parseDateInput(date: DateInput): Date {
	if (typeof date === "string") {
		const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
		if (dateOnly) {
			return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
		}
	}

	return new Date(date as string | number);
}

export function convertDateToWords(date: DateInput): string {
	const dateObj = parseDateInput(date);
	const month = months[dateObj.getMonth()];
	const year = dateObj.getFullYear();
	return `${month} ${year}`;
}

export function formatAPIDate(format: string, date: DateInput): string {
	const dateObj = new Date(date as string | number);
	const year = dateObj.getFullYear();
	const month = months[dateObj.getMonth()];
	const hour = dateObj.getHours() < 10 ? `0${dateObj.getHours()}` : dateObj.getHours();
	const min = dateObj.getMinutes() < 10 ? `0${dateObj.getMinutes()}` : dateObj.getMinutes();
	const day = dateObj.getDate() < 10 ? `0${dateObj.getDate()}` : dateObj.getDate();

	return format === "M-D-Y-T" ? `${month} ${day}, ${year} ${hour}:${min}` : `${month} ${day}, ${year}`;
}

export function convertParagraphsToList(text: string): string {
	return text.replace("<p>", "<li>").replace("</p>", "</li>");
}

export function dateToMYObject(date: DateInput): MonthYear {
	const dateObj = parseDateInput(date);
	return {
		month: dateObj.getMonth() + 1,
		year: dateObj.getFullYear(),
	};
}

export function currencyFormat(amount: string | number): string {
	return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function dateToMYDObject(date: DateInput): MonthYearDay {
	const dateObj = parseDateInput(date);
	return {
		month: dateObj.getMonth() + 1,
		year: dateObj.getFullYear(),
		day: dateObj.getDate(),
	};
}

export function dateObjToYMD(obj: DateObject): string {
	return dayjs(`${obj.year}-${obj.month}-${obj.day}`).format("MMM DD, YYYY");
}

const monthNames = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

export function dateToMY(date: Parameters<typeof dayjs>[0]): string {
	return dayjs(date).format("MMM YYYY");
}

export function dateToDMY(date: DateInput): string {
	const dateObj = parseDateInput(date);
	return `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
}

export function convertUlToArrayDefault(text: string): string[] {
	return (text as ReplaceAllString)
		.replaceAll("</li>", "")
		.replaceAll("<ul>", "")
		.replaceAll("<p><br></p>", "")
		.replaceAll("<br>", "")
		.replaceAll("</ul>", "")
		.split("<li>");
}

export function convertUlToArray(inputs: { toString(): string }): ListItem[] {
	const text = (inputs.toString() as ReplaceAllString)
		.replaceAll("</li>", "")
		.replaceAll("<ul>", "")
		.replaceAll("</ul>", "")
		.replaceAll("<p>", "")
		.replaceAll("</p>", "")
		.replaceAll("<br>", "")
		.replaceAll("<p><br></p>", "")
		.split("<li>");

	let itemData: any = text.reduce((prev: any, value: string): any => {
		return [
			...prev,
			{
				bp: true,
				value: value.trim(),
			},
		];
	});

	return (itemData = Array.isArray(itemData) ? itemData : [{ bp: false, value: itemData }]);
}

export function convertArrayToUl(array: ListItem[]): string {
	let list = "";

	array.forEach((element) => {
		if (element.bp) {
			list = list.concat("<ul><li>", element.value, "</li></ul>");
		} else {
			list = list.concat("<p>", element.value, "</p>");
		}
	});

	return list;
}

export function convertDateTo2Digits(date: number): string | number {
	return date < 10 ? `0${date}` : date;
}

export function logToConsole(data: unknown): void {
	// eslint-disable-next-line no-console
	return console.log(data);
}

export function searchJobFunction(query: string, jobFunction: string[]): string {
	// search job function array for query or something similar to it
	return jobFunction
		.some((item) => {
			return item.toLowerCase().includes(query.toLowerCase());
		})
		.toString();
}

export function formatAmount(amount: number): string {
	return amount.toLocaleString("en-US");
}

export function getSanitizedHtml(dirty: string): React.ReactElement {
	return React.createElement("span", {
		dangerouslySetInnerHTML: {
			__html: DOMPurify.sanitize(dirty, {
				ADD_ATTR: ["target"],
				ALLOWED_TAGS: ["b", "i", "a", "br", "ul", "li", "span"],
			}),
		},
	});
}

export function calculateDateToToday(date: DateInput): string {
	// To set two dates to two variables
	const createdDate = new Date(date as string | number);
	const todayDate = new Date();

	// To calculate the time difference of two dates
	const differenceInTime = todayDate.getTime() - createdDate.getTime();

	// To calculate the no. of days between two dates
	const differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24));

	if (differenceInDays <= 0) {
		return "Today";
	} else if (differenceInDays === 1) {
		return "Yesterday";
	} else {
		return `${differenceInDays} days ago`;
	}
}

export function calculateDayRemainsToDate(obj: DateObject): string {
	// To set two dates to two variables
	const createdDate = new Date(obj.year as number, (obj.month as number) - 1, obj.day as number);
	const todayDate = new Date();

	// To calculate the time difference of two dates
	const differenceInTime = createdDate.getTime() - todayDate.getTime();

	// To calculate the no. of days between two dates
	const differenceInDays = Math.round(differenceInTime / (1000 * 3600 * 24));

	if (differenceInDays <= 0) {
		return "Today";
	} else if (differenceInDays === 1) {
		return "Tomorrow";
	} else {
		return `${differenceInDays} days remains`;
	}
}

export function getTemplateName(templateId: string): string {
	const temId = templateId;
	//if contains q substring from q_to_end
	const indexOfq = temId.indexOf("q");
	let intermediateName = templateId;

	if (indexOfq >= 0) {
		intermediateName = intermediateName.substring(indexOfq + 1);
	}

	//replace x with _
	intermediateName = (intermediateName as ReplaceAllString).replaceAll("x", "_");

	return intermediateName;
}

export function match<T>(expression: string, cases: Record<string, T>): T {
	return cases[expression] || cases?.default;
}

export const getColumnName = (columns: string[], columnName: string): string => {
	const column = columns.find((column) => column === columnName);
	return column || "";
};

export function capitalizeFirstLetterOfWords(input: string): string {
	if (!input) return input;
	const words = input.split(" ");

	const capitalizedWords = words.map((word) => {
		if (word.length === 0) {
			return "";
		}
		const firstLetter = word[0].toUpperCase();
		const restOfWord = word.slice(1).toLowerCase();
		return firstLetter + restOfWord;
	});

	return capitalizedWords.join(" ");
}

export function getRewardPointTextColor(status: string): string {
	if (status === "PENDING") {
		return "text-warning";
	} else if (status === "CLAIMED") {
		return "text-success";
	} else {
		return "text-muted";
	}
}

export function calculateMaximumRedeemablePoints(packageName: string, rewards: RewardPoint[]): MaximumRedeemablePoints {
	const dprozPackage = packageNames[packageName];
	const maxRedeemablePoints = dprozPackage["maxRedeemablePoints"];
	const originalAmount = dprozPackage["originalAmount"];
	const points = rewards.reduce((total = 0, rewardPoint) => total + rewardPoint.rewardPoints, 0);
	const amountPaidByPoints = points > maxRedeemablePoints ? maxRedeemablePoints : points;
	let reducedAmountPaidByPoints = amountPaidByPoints;
	let redeemableRewardIds: string[] = [];
	const oneFiftyRewardPoint = 150;
	const fiftyRewardPoint = 50;
	const oneFiftyRewardPointItems = rewards.filter((item) => item.rewardPoints == oneFiftyRewardPoint);
	const fiftyRewardPointItems = rewards.filter((item) => item.rewardPoints == fiftyRewardPoint);
	const selectedRewardItems: RewardPoint[] = [];

	if (oneFiftyRewardPointItems.length !== 0 || fiftyRewardPointItems.length !== 0) {
		if (amountPaidByPoints > fiftyRewardPoint && oneFiftyRewardPointItems.length > 0) {
			for (let index = 0; index < oneFiftyRewardPointItems.length; index++) {
				const rewardItem = oneFiftyRewardPointItems[index];
				if (reducedAmountPaidByPoints > 0 && rewardItem.rewardPoints > 0) {
					if (rewardItem.rewardPoints > reducedAmountPaidByPoints) break;
					selectedRewardItems.push(rewardItem);
					reducedAmountPaidByPoints = reducedAmountPaidByPoints - rewardItem.rewardPoints;
				}
			}
		}

		if (reducedAmountPaidByPoints > 0 && fiftyRewardPointItems.length > 0) {
			for (let index = 0; index < fiftyRewardPointItems.length; index++) {
				const rewardItem = fiftyRewardPointItems[index];
				if (reducedAmountPaidByPoints > 0 && rewardItem.rewardPoints > 0) {
					if (rewardItem.rewardPoints > reducedAmountPaidByPoints) break;
					selectedRewardItems.push(rewardItem);
					reducedAmountPaidByPoints = reducedAmountPaidByPoints - rewardItem.rewardPoints;
				}
			}
		}
	}
	redeemableRewardIds = selectedRewardItems.map((item) => item._id);

	return {
		amountPaidByPoints: amountPaidByPoints,
		amount: originalAmount - amountPaidByPoints,
		originalAmount: originalAmount,
		rewards: rewards,
		totalAvailablePoints: points,
		redeemableRewardIds: redeemableRewardIds,
	};
}

export function urlify(text: string): string {
	var urlRegex = /(https?:\/\/[^\s]+)/g;
	return text.replace(urlRegex, function (url: string) {
		return `
		<br/>
		<div class="d-flex align-items-center justify-content-center">
		  <a class="btn btn-primary text-center" href="${url}" target="_blank" rel="nofollow noreferrer noopener">Click to Apply</a>
		</div>
		`;
	});
}

export function removeHTTPAndDomainName(item: SocialLink): string | undefined {
	if (item.type === "linkedIn") {
		return item?.value?.replace("https://", "")?.replace("www.", "")?.replace("linkedin.com/in/", "");
	} else if (item.type === "twitter") {
		return item?.value?.replace("https://", "")?.replace("twitter.com/", "");
	} else if (item.type === "github") {
		return item?.value?.replace("https://", "")?.replace("github.com/", "");
	} else {
		return item?.value?.replace("https://", "")?.replace("www.", "");
	}
}

export function getFirstWordSeparedByFullColon(text: string): string {
	const splitArray = text.split(":");
	let wordBeforeColon = splitArray[0].trim();
	return wordBeforeColon;
}

export function convertSplittedSkillsToNormalSkillsArray(skillsArray: SplitSkill[]): ListItem[] {
	return skillsArray.map((skill) => ({
		bp: true,
		value: `${skill.title}: ${skill.skills
			.filter((item) => item.added)
			.map((item) => item.skill)
			.join(", ")}`,
	}));
}

export function getNestedProperty(obj: IndexableObject, path: string): any {
	return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

export function getSectionName(obj: IndexableObject, sectionName: string): any {
	if (sectionName === sections.SUMMARY) {
		return getNestedProperty(obj, "summary");
	}
	if (sectionName === sections.SKILLS) {
		return getNestedProperty(obj, "skills");
	}
	if (sectionName === sections.CERTIFICATION) {
		return getNestedProperty(obj, "extras.certifications");
	}
	if (sectionName === sections.ACCOMPLISHMENTS) {
		return getNestedProperty(obj, "extras.accomplishments");
	}
	if (sectionName === sections.AFFILIATION) {
		return getNestedProperty(obj, "extras.affiliations");
	}
	if (sectionName === sections.ADDITIONAL_INFO) {
		return getNestedProperty(obj, "extras.additionalInfo");
	}
	if (sectionName === sections.CUSTOM) {
		return getNestedProperty(obj, "extras.customExtras.data");
	}
	return getNestedProperty(obj, "summary");
}

export function getResumeSectionPositionFromTop(element: Element | null): number {
	if (!element) return 0;
	const rect = element.getBoundingClientRect();
	const scrollTop = window.scrollY;
	const elementTopPosition = rect.top + scrollTop - 80;
	return elementTopPosition;
}

export function isNotEmptyCustomReasonsOrRecommendations(reasonsOrRecommendations: string): boolean {
	if (convertUlToArray(reasonsOrRecommendations).length === 0) return false;
	return convertUlToArray(reasonsOrRecommendations)[0].value.length > 0;
}

export function isNotEmptyValue(value: { length: number }): boolean {
	return value.length > 0;
}

export function getInitials(name: string = ""): string {
	return name
		.replace(/\s+/, " ")
		.split(" ")
		.slice(0, 2)
		.map((v) => v && v[0].toUpperCase())
		.join("");
}

export function checkObjectsHaveSameKeysAndValues(
	obj1: Record<string, unknown> | null | undefined,
	obj2: Record<string, unknown> | null | undefined,
): boolean {
	const keys1 = Object.keys(obj1 || {});
	const keys2 = Object.keys(obj2 || {});

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (let key of keys1) {
		if (obj1[key] !== obj2[key]) {
			return false;
		}
	}

	return true;
}

export function removeTextAfterAt(inputString: string): string {
	const atIndex = inputString.indexOf("at");
	if (atIndex !== -1) {
		return inputString.substring(0, atIndex).trim();
	}
	return inputString;
}

export function convertArrayStringToArray(arrayString: string): ListItem[] {
	let arrayData: ListItem[] = [];
	try {
		const parsedData = JSON.parse(arrayString);
		if (Array.isArray(parsedData)) {
			arrayData = parsedData.map((item) => {
				return { bp: true, value: item };
			});
		}
	} catch (error) {
		console.error("Error parsing array string:", error);
		arrayData.push({ bp: true, value: arrayString });
	}
	return arrayData;
}

export function getLinkFromString(text: string): string {
	const urlPattern = /(https?:\/\/[^\s]+)/;
	const match = text.match(urlPattern);
	return match ? match[0] : "";
}

export function compareArray(firstArray: unknown[] | null, secondArray: unknown[] | null): boolean {
	if (!firstArray || !secondArray) return false;
	const sortFirstArray = [...firstArray]?.slice()?.sort();
	const sortSecondArray = [...secondArray]?.slice()?.sort();

	return JSON.stringify(sortFirstArray) !== JSON.stringify(sortSecondArray);
}

// Function to generate SHA-256 hash of the file
export function generateFileHash(file: Blob): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();

		reader.onload = (event) => {
			const wordArray = CryptoJS.lib.WordArray.create(event.target.result as ArrayBuffer);
			const hash = CryptoJS.SHA256(wordArray).toString();
			resolve(hash);
		};

		reader.onerror = (error) => reject(error);

		reader.readAsArrayBuffer(file);
	});
}

export function generateYearArray(startYear: number = 1980): number[] {
	const currentYear = new Date().getFullYear();
	const yearArray: number[] = [];

	for (let year = startYear; year <= currentYear; year++) {
		yearArray.push(year);
	}

	return yearArray.reverse();
}

export function getBrowserName(): string {
	const userAgent = navigator.userAgent;

	if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) return "Google Chrome";
	if (userAgent.includes("Firefox")) return "Mozilla Firefox";
	if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) return "Safari";
	if (userAgent.includes("Edg")) return "Microsoft Edge";
	if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "Opera";
	if (userAgent.includes("Trident") || userAgent.includes("MSIE")) return "Internet Explorer";

	return "Unknown Browser";
}

export function getOperatingSystem(): string {
	const userAgent = navigator.userAgent || navigator.vendor || (window as WindowWithOpera).opera;

	if (/windows nt 10/i.test(userAgent)) return "Windows 10/11";
	if (/windows nt 6.3/i.test(userAgent)) return "Windows 8.1";
	if (/windows nt 6.2/i.test(userAgent)) return "Windows 8";
	if (/windows nt 6.1/i.test(userAgent)) return "Windows 7";
	if (/windows nt 6.0/i.test(userAgent)) return "Windows Vista";
	if (/windows nt 5.1/i.test(userAgent)) return "Windows XP";
	if (/macintosh|mac os x/i.test(userAgent)) return "Mac OS";
	if (/android/i.test(userAgent)) return "Android";
	if (/iphone|ipad|ipod/i.test(userAgent)) return "iOS";
	if (/linux/i.test(userAgent)) return "Linux";

	return "Unknown OS";
}

export function formatExtraSections(sectionName: string): string {
	if (sectionName === "accomplishments") {
		return "Accomplishments";
	} else if (sectionName === "affiliations") {
		return "Affiliations";
	} else if (sectionName === "personalLinks") {
		return "Personal Links";
	} else if (sectionName === "certifications") {
		return "Certifications";
	} else if (sectionName === "additionalInfo") {
		return "Additional Info";
	} else if (sectionName === "languages") {
		return "Languages";
	} else if (sectionName === "references") {
		return "References";
	} else if (sectionName === "personalProfile") {
		return "Personal Profile";
	} else if (sectionName === "customExtras") {
		return "Custom Extras";
	}
	return sectionName;
}

export function isMobile(): boolean {
	return (
		typeof window !== "undefined" &&
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)
	);
}

// Date calculation helpers for FitCheck stats
export function getStartOfWeek(date: DateInput): Date {
	const d = new Date(date as string | number);
	const day = d.getDay();
	const diff = d.getDate() - day; // Sunday = 0
	d.setDate(diff);
	d.setHours(0, 0, 0, 0);
	return d;
}

export function getStartOfMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

export function getStartOfLastMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth() - 1, 1, 0, 0, 0, 0);
}

export function getEndOfLastMonth(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 0, 23, 59, 59, 999);
}

export function formatRelativeTime(dateString: DateInput): string {
	const date = new Date(dateString as string | number);
	const now = new Date();
	const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

	if (diffInDays === 0) return "Today";
	if (diffInDays === 1) return "1 day ago";
	if (diffInDays < 7) return `${diffInDays} days ago`;
	if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
	return `${Math.floor(diffInDays / 30)} months ago`;
}

/**
 * An ALL-CAPS stored value rendered for a reader: "SEPERATED" -> "Seperated".
 *
 * The resume templates print enum members straight onto the finished document,
 * where shouting looks like a defect. This only changes the case — it does not
 * correct spelling, so a misspelled enum member stays recognisably itself.
 */
export function toTitleCase(value?: string | null): string {
	if (!value) return "";

	return value
		.toLowerCase()
		.split(/(\s+|\/)/)
		.map((part) => (/^[a-z]/.test(part) ? part.charAt(0).toUpperCase() + part.slice(1) : part))
		.join("");
}

/**
 * A skill as it should read on a resume, without the source data's grouping
 * punctuation.
 *
 * `skills/*.json` wraps each category's members in braces and sometimes nests a
 * sub-list in brackets — "Research : { Documentation, Data collection[ Interviews
 * ] }". The work-details picker already strips those, but the Skills screen
 * stored them verbatim, so a finished resume printed the braces.
 */
export function stripSkillGrouping(skill: string): string {
	return skill
		.replace(/[{}[\]]/g, "")
		.replace(/\s+/g, " ")
		.trim();
}
